/**
 * EO «Финансы» — ответ GET /api/finance/analytics → модель экрана (useFinanceAnalytics.ts). Без React и запросов.
 * Форма ответа — apps/api/src/routes/finance.ts (/analytics): snake_case, completed/futureBooked могут быть {}.
 * Все числа нормализуем Number()||0 — D1 может отдать null в COALESCE-дырах.
 * Тест: financeAnalytics.test.ts; связка с настоящим ответом — apps/api/src/routes/finance-other1c.test.ts.
 */

export interface FinCompleted {
  revenue: number; cash: number; terminal: number; transfer: number;
  prepaid: number; invoice: number; gamesDone: number;
  /** Архив 1С (В8, 16.10): «Друг.» отчётов V4 и сумма игр 1С (вид оплаты неизвестен) — utils/payOther1c.ts на сервере. */
  other: number; other1c: number;
}
export interface FinBranchRow { branchId: string; branchName: string; revenue: number; gamesDone: number }
export interface FinEstBranchRow { branchName: string; est: number; count: number }
export interface FinFutureBooked { count: number; players: number; prepaidCollected: number; noPrepaidCount: number }
export interface FinDailyPoint { date: string; revenue: number; games: number }
export interface FinQuestRow { questId: string; questShort: string; branchName: string; games: number; revenue: number }
export interface FinFuturePoint { date: string; count: number }

export interface FinAnalytics {
  completed: FinCompleted;
  byBranch: FinBranchRow[];
  estByBranch: FinEstBranchRow[];
  futureBooked: FinFutureBooked;
  estRevenue: number;
  allBookedPlayers: number;
  allBookedCount: number;
  byQuest: FinQuestRow[];
  conducted: number;
  cancelled: number;
  dailyRevenue: FinDailyPoint[];
  futureByDate: FinFuturePoint[];
}

/* Wire-форма (snake_case, все поля опциональны — completed/futureBooked могут быть {}). */
export interface RawAnalytics {
  error?: string;
  completed?: { revenue?: number; cash?: number; terminal?: number; transfer?: number; prepaid?: number; invoice?: number; games_done?: number;
    other?: number; other_1c?: number };
  byBranch?: { branch_id?: string; branch_name?: string; revenue?: number; games_done?: number }[];
  futureBooked?: { count?: number; players?: number; prepaid_collected?: number; no_prepaid_count?: number };
  estRevenue?: number;
  estByBranch?: { branch_name?: string; est?: number; count?: number }[];
  byQuest?: { quest_id?: string; quest_short?: string; branch_name?: string; games?: number; revenue?: number }[];
  allBooked?: { count?: number; players?: number };
  conducted?: number;
  cancelled?: number;
  dailyRevenue?: { date?: string; revenue?: number; games?: number }[];
  futureByDate?: { date?: string; count?: number }[];
}

const num = (v: unknown): number => Number(v) || 0;

/** Ответ /analytics (без error) → модель экрана. */
export function normalizeAnalytics(r: RawAnalytics): FinAnalytics {
  const c = r.completed ?? {};
  const fb = r.futureBooked ?? {};
  return {
    completed: {
      revenue: num(c.revenue), cash: num(c.cash), terminal: num(c.terminal),
      transfer: num(c.transfer), prepaid: num(c.prepaid), invoice: num(c.invoice),
      gamesDone: num(c.games_done),
      other: num(c.other), other1c: num(c.other_1c),   // старый сервер их не отдаёт — 0, блок как раньше
    },
    byBranch: (r.byBranch ?? []).map((b) => ({
      branchId: String(b.branch_id ?? ""), branchName: String(b.branch_name ?? "—"),
      revenue: num(b.revenue), gamesDone: num(b.games_done),
    })),
    estByBranch: (r.estByBranch ?? []).map((b) => ({
      branchName: String(b.branch_name ?? "—"), est: num(b.est), count: num(b.count),
    })),
    futureBooked: {
      count: num(fb.count), players: num(fb.players),
      prepaidCollected: num(fb.prepaid_collected), noPrepaidCount: num(fb.no_prepaid_count),
    },
    estRevenue: num(r.estRevenue),
    allBookedPlayers: num(r.allBooked?.players),
    allBookedCount: num(r.allBooked?.count),
    byQuest: (r.byQuest ?? []).map((q) => ({
      questId: String(q.quest_id ?? ""), questShort: String(q.quest_short ?? "—"),
      branchName: String(q.branch_name ?? ""), games: num(q.games), revenue: num(q.revenue),
    })),
    conducted: num(r.conducted),
    cancelled: num(r.cancelled),
    dailyRevenue: (r.dailyRevenue ?? []).map((d) => ({ date: String(d.date ?? ""), revenue: num(d.revenue), games: num(d.games) })),
    futureByDate: (r.futureByDate ?? []).map((d) => ({ date: String(d.date ?? ""), count: num(d.count) })),
  };
}
